import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FlexRow } from '@/components/ui/flex';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useUpdateSharedKnowledgeFileService } from '@/services/sharedFiles';
import { browserDownload } from '@/utils/downloadFileUtils';
import { type SharedKnowledgeFileDto } from '@ditto/shared';
import { TanstackSortableColumnHeader } from '@/components/table/SortColumnHeader';
import { ColumnDef } from '@tanstack/react-table';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import { Download, Ellipsis } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { DeleteFileModal } from '../DeleteFileModal';
import { EditFileModal } from '../EditFileModal';

export const useColumns = (): ColumnDef<SharedKnowledgeFileDto>[] => {
  return [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={value => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={value => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <TanstackSortableColumnHeader column={column}>
          File Name
        </TanstackSortableColumnHeader>
      ),
      cell: ({ row }) => (
        <Link to={`/admin/files/${row.original.id}`}>
          <p className="font-bold">{row.original.name}</p>
        </Link>
      ),
    },
    {
      accessorKey: 'group',
      header: ({ column }) => (
        <TanstackSortableColumnHeader column={column}>
          Knowledge group
        </TanstackSortableColumnHeader>
      ),
      cell: ({ row }) => {
        return <div>{row.original.group?.name || 'No Group'}</div>;
      },
    },
    {
      accessorKey: 'shouldBeUsedByAI',
      header: 'Should be used by AI',
      cell: ({ row }) => {
        const { updateFile } = useUpdateSharedKnowledgeFileService();
        const handleChange = (checked: boolean) => {
          updateFile({
            fileId: row.original.id,
            patch: {
              shouldBeUsedByAI: checked,
            },
          });
        };

        return (
          <Tooltip>
            <TooltipTrigger>
              <Switch
                checked={row.original.shouldBeUsedByAI}
                onCheckedChange={handleChange}
              />
            </TooltipTrigger>
            <TooltipContent className="max-w-[300px]">
              Allow the AI to use this file. You can disable this option if you
              do not want the AI to use this file (file in progress, not
              relevant, etc.).
            </TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const [isOpen, setIsOpen] = useState(false);
        const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

        const handleEditClick = () => {
          setIsOpen(true);
        };

        const handleDeleteClick = () => {
          setIsDeleteModalOpen(true);
        };

        const handleDownloadClick = () => {
          browserDownload(row.original.signedUrl, row.original.name);
        };

        return (
          <FlexRow gap="2">
            <Button variant="outline" size="icon" onClick={handleDownloadClick}>
              <Download />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <Ellipsis />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="start">
                <DropdownMenuItem onClick={handleEditClick}>
                  {'Edit'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDeleteClick}>
                  {'Delete'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <EditFileModal
              open={isOpen}
              onOpenChange={setIsOpen}
              fileId={row.original.id}
              initialGroupId={row.original.group?.id ?? null}
              initialName={row.original.name}
            />
            <DeleteFileModal
              fileId={row.original.id}
              open={isDeleteModalOpen}
              onOpenChange={setIsDeleteModalOpen}
            />
          </FlexRow>
        );
      },
    },
  ];
};
